// Proje Detay — plan §4.3.5 + design ref #screen-project-detail.
// /projects/:id — üst özet kart + bölüm listesi (snapshot'tan) + ekip panel placeholder +
// ana aksiyon butonları (Forma Devam Et M4 / Mevcut Hali İndir M4-M7 / AI Üretimi Başlat M6).

import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  formatReportingPeriod,
  isAnswerComplete,
  type DocumentExportResponse,
  type DocumentInfo,
  type DocumentListResponse,
  type FormResponsesListResponse,
  type Project,
  type ProjectProvenanceResponse,
  type ProjectStatus,
} from "@mahir/shared";
import { api, ApiError } from "../api/client";
import { getProjectProvenance } from "../api/report-import";
import { GenerateConfirmModal } from "../components/generation/GenerateConfirmModal";
import { FillFromReportModal } from "../components/project/FillFromReportModal";
import { ImportSummarySection } from "../components/project/ImportSummarySection";
import { ParticipantsCard } from "../components/project/ParticipantsCard";
import { ProjectAuditWidget } from "../components/project/ProjectAuditWidget";
import { DocumentKpiLink } from "../components/project/DocumentKpiLink";
import { ProjectKpiSetField } from "../components/project/ProjectKpiSetField";
import { ProjectActions } from "../components/project/ProjectActions";
import { SECTION_TONE_COLOR, sectionTone } from "../lib/form-fill-copy";
import { IMPORT_COPY, hasActiveImport } from "../lib/report-import-ui";
import { useAuthStore } from "../stores/auth-store";
import { DRAFT_PREVIEW_BADGE, documentTypeLabel, isDraftPreview } from "../lib/document-label";

interface SectionSnapshot {
  id: string;
  code: string;
  label: string;
  description: string | null;
  order: number;
}

interface QuestionSnapshotMin {
  sectionId: string;
  code: string;
  id: string;
  required: boolean;
  type: string;
  options: unknown;
  enabled?: boolean;
}

interface SnapshotResponse {
  project: Project;
  snapshot: {
    sections: SectionSnapshot[];
    questions: QuestionSnapshotMin[];
  } | null;
}

const STATUS_LABELS: Record<ProjectStatus, string> = {
  draft: "Taslak",
  in_progress: "Sürüyor",
  completed: "Tamamlandı",
};

const STATUS_COLORS: Record<ProjectStatus, { bg: string; fg: string }> = {
  draft: { bg: "var(--color-bg)", fg: "var(--color-text-3)" },
  in_progress: {
    bg: "color-mix(in srgb, var(--color-primary) 14%, white)",
    fg: "var(--color-primary)",
  },
  completed: {
    bg: "color-mix(in srgb, var(--color-success) 10%, white)",
    fg: "var(--color-success)",
  },
};

export function ProjectDetail(): JSX.Element {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState<Project | null>(null);
  const [snapshot, setSnapshot] = useState<SnapshotResponse["snapshot"]>(null);
  const [responses, setResponses] = useState<FormResponsesListResponse["responses"]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [generateModalOpen, setGenerateModalOpen] = useState(false);
  const [regenerateModalOpen, setRegenerateModalOpen] = useState(false);
  const [currentVersion, setCurrentVersion] = useState(0);
  // #014/#017/#018/#019 lifecycle hardening — review state ihtiyacı
  const [reviewSummary, setReviewSummary] = useState<{ total: number; approved: number } | null>(
    null,
  );
  // #024 DOCX history — reopen sonrası eski versiyon raporları
  const [documents, setDocuments] = useState<DocumentInfo[]>([]);
  // Task 14 — Geçmiş Rapor Aktarımı: "Rapordan doldur" + ImportSummarySection.
  const [provenance, setProvenance] = useState<ProjectProvenanceResponse | null>(null);
  const [provenanceError, setProvenanceError] = useState(false);
  const [fillModalOpen, setFillModalOpen] = useState(false);
  const [fillQueuedBand, setFillQueuedBand] = useState(false);
  const [provenanceRefreshKey, setProvenanceRefreshKey] = useState(0);

  const user = useAuthStore((s) => s.user);
  const canReopen = user?.permissions["projects.reopen"] === true;
  const canImportReport = user?.permissions["projects.import_report"] === true;

  useEffect(() => {
    if (!id) return;
    let aborted = false;
    // Generation version fetch (ikincil buton "Tümünü yeniden üret" karar #3)
    api<{ active: boolean; version: number }>(`/projects/${id}/generate/active`)
      .then((r) => {
        if (!aborted) setCurrentVersion(r.version);
      })
      .catch(() => {
        // Ignore — endpoint 404 olabilir proje yoksa
      });
    setLoading(true);
    // Project + snapshot + responses + review summary + documents paralel.
    Promise.all([
      api<{ project: Project }>(`/projects/${id}`),
      api<SnapshotResponse>(`/projects/${id}/snapshot`).catch(() => ({
        project: null as unknown as Project,
        snapshot: null,
      })),
      api<FormResponsesListResponse>(`/projects/${id}/responses`).catch(() => ({
        responses: [] as FormResponsesListResponse["responses"],
      })),
      // #018 Final DOCX gate + #019 Tamamla buton gate için review state şart
      api<{ sections: { reviewStatus: string }[] }>(`/projects/${id}/review`).catch(() => ({
        sections: [] as { reviewStatus: string }[],
      })),
      // #024 DOCX history — superseded dahil
      api<DocumentListResponse>(`/projects/${id}/documents?includeSuperseded=true`).catch(() => ({
        documents: [] as DocumentInfo[],
      })),
    ])
      .then(([p, snap, resp, rev, docs]) => {
        if (aborted) return;
        setProject(p.project);
        setSnapshot(snap.snapshot);
        setResponses(resp.responses);
        const total = rev.sections.length;
        const approved = rev.sections.filter((s) => s.reviewStatus === "approved").length;
        setReviewSummary({ total, approved });
        setDocuments(docs.documents);
        setError(null);
      })
      .catch((err) => {
        if (!aborted) setError(err instanceof Error ? err.message : "Proje yüklenemedi");
      })
      .finally(() => {
        if (!aborted) setLoading(false);
      });
    return () => {
      aborted = true;
    };
  }, [id]);

  // Task 14 — köken (`provenance`) ayrı istek: `ImportSummarySection` + "Rapordan
  // doldur" düğmesinin aktif-import kontrolü buna bağlı. Ana yüklemeyi bloklamaz;
  // `provenanceRefreshKey` fill kuyruğa alındıktan sonra yeniden çeker (ikinci
  // fill'in de düğmeyi doğru kilitlemesi/açması için).
  useEffect(() => {
    if (!id) return;
    let aborted = false;
    getProjectProvenance(id)
      .then((res) => {
        if (!aborted) {
          setProvenance(res);
          setProvenanceError(false);
        }
      })
      .catch(() => {
        if (!aborted) setProvenanceError(true);
      });
    return () => {
      aborted = true;
    };
  }, [id, provenanceRefreshKey]);

  // Fix round 1 (T14 review) — aktif import (queued…writing) sürerken provenance'ı
  // 5 sn'de bir poll et ki düğme + ImportSummarySection sayfa yenilemeden güncellensin.
  // JobsTab'ın SSE kanalına (`REPORT_IMPORT_EVENTS_URL`) bağlanmaya gerek yok — tek
  // proje, düşük frekans; her `provenance` güncellemesinde effect yeniden değerlendirir,
  // aktif import kalmayınca doğal olarak interval kurulmaz (poll durur).
  useEffect(() => {
    if (!id) return;
    if (!hasActiveImport(provenance?.imports ?? [])) return;
    let aborted = false;
    const timer = window.setInterval(() => {
      getProjectProvenance(id)
        .then((res) => {
          if (!aborted) {
            setProvenance(res);
            setProvenanceError(false);
          }
        })
        .catch(() => {
          // Poll sırasında geçici hata — sessizce bir sonraki tick'i bekle.
        });
    }, 5000);
    return () => {
      aborted = true;
      window.clearInterval(timer);
    };
  }, [id, provenance]);

  useEffect(() => {
    if (!fillQueuedBand) return;
    const t = window.setTimeout(() => setFillQueuedBand(false), 6000);
    return () => window.clearTimeout(t);
  }, [fillQueuedBand]);

  const progressBySection = useMemo(() => {
    const map = new Map<string, { answered: number; total: number }>();
    if (!snapshot) return map;
    const responseByCode = new Map<string, unknown>();
    for (const r of responses) responseByCode.set(r.questionCode, r.value);
    for (const q of snapshot.questions) {
      if (q.enabled === false) continue;
      const bucket = map.get(q.sectionId) ?? { answered: 0, total: 0 };
      bucket.total += 1;
      if (isAnswerComplete(responseByCode.get(q.code), q)) bucket.answered += 1;
      map.set(q.sectionId, bucket);
    }
    return map;
  }, [snapshot, responses]);

  if (loading)
    return (
      <div className="p-8" style={{ color: "var(--color-text-2)" }}>
        Yükleniyor…
      </div>
    );
  if (error || !project) {
    return (
      <div className="p-8">
        <div
          className="max-w-2xl px-4 py-3 rounded-lg text-sm"
          style={{
            background: "color-mix(in srgb, var(--color-error) 10%, white)",
            color: "var(--color-error)",
          }}
        >
          {error ?? "Proje bulunamadı"}
        </div>
        <Link
          to="/projects"
          className="inline-block mt-4 text-sm"
          style={{ color: "var(--color-primary)" }}
        >
          ← Proje listesine dön
        </Link>
      </div>
    );
  }

  const statusColor = STATUS_COLORS[project.status];
  const statusLabel = STATUS_LABELS[project.status];
  const importActive = hasActiveImport(provenance?.imports ?? []);

  return (
    <div className="p-8 mx-auto" style={{ fontFamily: "var(--font-sans)", maxWidth: 1200 }}>
      <Link to="/projects" className="text-sm" style={{ color: "var(--color-primary)" }}>
        ← Proje listesine dön
      </Link>

      {/* Üst özet kart + ana aksiyonlar */}
      <div
        className="mt-4 mb-6 rounded-xl p-6"
        style={{
          background: "var(--color-surface)",
          border: "1px solid var(--color-border)",
        }}
      >
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div className="min-w-0" style={{ flex: "1 1 280px" }}>
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <span
                style={{
                  padding: "2px 10px",
                  borderRadius: 999,
                  background: statusColor.bg,
                  color: statusColor.fg,
                  fontSize: "0.72rem",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                }}
              >
                {statusLabel}
              </span>
              <span
                style={{
                  fontFamily: "var(--font-mono)",
                  fontSize: "0.75rem",
                  color: "var(--color-text-3)",
                }}
              >
                {project.versionNumber} ·{" "}
                {formatReportingPeriod(project.reportingYear, project.periodType)}
              </span>
            </div>
            <h1
              style={{
                fontFamily: "var(--font-display)",
                fontSize: "1.8rem",
                color: "var(--color-text)",
              }}
            >
              {project.clientName}
            </h1>
            <p className="mt-1 text-sm" style={{ color: "var(--color-text-2)" }}>
              {project.frameworkName} ·{" "}
              {formatReportingPeriod(project.reportingYear, project.periodType)}
            </p>
            <div
              className="mt-3 text-sm flex gap-3 flex-wrap"
              style={{ color: "var(--color-text-3)" }}
            >
              <span>
                Danışman:{" "}
                <strong style={{ color: "var(--color-text-2)" }}>{project.assignedToName}</strong>
              </span>
              <span>·</span>
              <span>
                Oluşturulma:{" "}
                {new Date(project.createdAt).toLocaleDateString("tr-TR", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </span>
            </div>

            {/* Task 14 — "Rapor aktarımı" alanı (3A: tek ImportSummarySection). */}
            <ImportSummarySection
              provenance={provenance}
              provenanceError={provenanceError}
              canOpenReport={canImportReport}
            />

            {fillQueuedBand && (
              <div className="mt-3 text-sm" style={{ color: "var(--color-text-2)" }}>
                Aktarım kuyruğa alındı —{" "}
                <Link
                  to="/projects/import?tab=jobs"
                  style={{
                    color: "var(--color-primary)",
                    textDecoration: "underline",
                    fontWeight: 600,
                  }}
                >
                  {IMPORT_COPY.tabJobs}
                </Link>
              </div>
            )}
          </div>
          <ProjectActions
            projectId={id}
            project={project}
            onProjectChange={setProject}
            reviewSummary={reviewSummary}
            currentVersion={currentVersion}
            canReopen={canReopen}
            canImportReport={canImportReport}
            importActive={importActive}
            onOpenGenerate={() => setGenerateModalOpen(true)}
            onOpenRegenerate={() => setRegenerateModalOpen(true)}
            onOpenFill={() => setFillModalOpen(true)}
            onDocumentsChanged={() => {
              // P3-30: "Üretilen Raporlar" + "KPI tablosu (Excel)" sayfa yenilenmeden görünsün
              api<DocumentListResponse>(`/projects/${id}/documents?includeSuperseded=true`)
                .then((docs) => setDocuments(docs.documents))
                .catch(() => undefined);
            }}
          />
        </div>
      </div>

      <div className="grid gap-6 project-detail-grid">
        {/* Bölüm listesi */}
        <section>
          <h2
            className="mb-3"
            style={{
              fontFamily: "var(--font-display)",
              color: "var(--color-text)",
              fontSize: "1.2rem",
            }}
          >
            Bölümler
          </h2>
          {snapshot ? (
            <div
              className="rounded-xl overflow-hidden"
              style={{
                background: "var(--color-surface)",
                border: "1px solid var(--color-border)",
              }}
            >
              {snapshot.sections.length === 0 ? (
                <div className="p-6 text-center" style={{ color: "var(--color-text-3)" }}>
                  Snapshot boş.
                </div>
              ) : (
                snapshot.sections
                  .sort((a, b) => a.order - b.order)
                  .map((s, i) => {
                    const p = progressBySection.get(s.id) ?? { answered: 0, total: 0 };
                    const pct = p.total === 0 ? 0 : Math.round((p.answered / p.total) * 100);
                    // Ö3 — kısmi bölüm amber; kırmızı yalnız gerçek hata için.
                    const color = SECTION_TONE_COLOR[sectionTone(p)];
                    return (
                      <div
                        key={s.id}
                        style={{
                          padding: "12px 16px",
                          borderTop: i === 0 ? "none" : "1px solid var(--color-border)",
                          display: "flex",
                          alignItems: "center",
                          gap: 12,
                        }}
                      >
                        <span
                          style={{
                            fontFamily: "var(--font-mono)",
                            fontSize: "0.78rem",
                            color: "var(--color-text-3)",
                            minWidth: 80,
                          }}
                        >
                          {s.code}
                        </span>
                        <span style={{ flex: 1, color: "var(--color-text)", fontSize: "0.9rem" }}>
                          {s.label}
                        </span>
                        <span
                          style={{
                            fontSize: "0.78rem",
                            color,
                            fontFamily: "var(--font-mono)",
                            minWidth: 100,
                            textAlign: "right",
                          }}
                        >
                          {p.answered}/{p.total} · %{pct}
                        </span>
                      </div>
                    );
                  })
              )}
            </div>
          ) : (
            <div
              className="p-6 rounded-xl text-sm"
              style={{
                background: "var(--color-surface)",
                border: "1px dashed var(--color-border)",
                color: "var(--color-text-3)",
              }}
            >
              Proje bölümleri yüklenemedi. Sayfayı yenileyin veya yöneticinize başvurun.
            </div>
          )}
        </section>

        {/* Ekip panel placeholder */}
        <aside>
          <h2
            className="mb-3"
            style={{
              fontFamily: "var(--font-display)",
              color: "var(--color-text)",
              fontSize: "1.2rem",
            }}
          >
            Ekip
          </h2>
          <div
            className="rounded-xl p-4"
            style={{
              background: "var(--color-surface)",
              border: "1px solid var(--color-border)",
            }}
          >
            <div className="flex items-center gap-3">
              <div
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: "50%",
                  background: "var(--color-primary)",
                  color: "var(--color-surface)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "0.8rem",
                  fontWeight: 600,
                }}
              >
                {initials(project.assignedToName)}
              </div>
              <div className="min-w-0">
                <div style={{ color: "var(--color-text)", fontSize: "0.88rem", fontWeight: 500 }}>
                  {project.assignedToName}
                </div>
                <div style={{ color: "var(--color-text-3)", fontSize: "0.72rem" }}>
                  Sorumlu danışman
                </div>
              </div>
            </div>
            <p
              className="mt-4 pt-4 text-xs"
              style={{
                color: "var(--color-text-3)",
                borderTop: "1px solid var(--color-border)",
              }}
            >
              Şu an sadece sorumlu danışman atanmış.
            </p>
          </div>
          {/* TODO-KPI-12 — KPI seti bağla/değiştir/kaldır; tamamlanmış projede salt okunur. */}
          <ProjectKpiSetField project={project} onUpdated={setProject} />
        </aside>
      </div>

      {/* #024 — Üretilen Raporlar (DOCX history). Full-width grid, bölümlerin altında.
          Reopen sonrası eski versiyonlar "eski sürüm" badge'iyle görünür — silinmez. */}
      {documents.length > 0 && (
        <section className="mt-8">
          <div className="flex items-baseline justify-between mb-3">
            <h2
              className="text-xl"
              style={{ fontFamily: "var(--font-display)", color: "var(--color-text)" }}
            >
              Üretilen Raporlar
              <span
                style={{
                  marginLeft: 8,
                  fontSize: "0.85rem",
                  color: "var(--color-text-3)",
                  fontWeight: 400,
                }}
              >
                ({documents.length.toString()})
              </span>
            </h2>
            <span className="text-xs" style={{ color: "var(--color-text-3)" }}>
              "Eski sürüm" rotuyla yeniden açma sonrası arşivlenmiş versiyonlar
            </span>
          </div>
          <div
            style={{
              display: "grid",
              gap: 12,
              gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
            }}
          >
            {documents.map((d) => {
              const date = new Date(d.generatedAt).toLocaleDateString("tr-TR", {
                day: "2-digit",
                month: "short",
                year: "numeric",
              });
              const time = new Date(d.generatedAt).toLocaleTimeString("tr-TR", {
                hour: "2-digit",
                minute: "2-digit",
              });
              const isFull = d.type === "full";
              return (
                <div key={d.id} className="flex flex-col gap-1">
                  <button
                    onClick={() => {
                      api<DocumentExportResponse>(`/projects/${id}/documents/${d.id}`)
                        .then((res) => window.open(res.downloadUrl, "_blank"))
                        .catch((err: unknown) => {
                          if (err instanceof ApiError) alert(err.message);
                        });
                    }}
                    className="text-left rounded-xl p-4"
                    style={{
                      background: d.supersededAt
                        ? "var(--color-bg)"
                        : isFull
                          ? "color-mix(in srgb, var(--color-success) 6%, var(--color-surface))"
                          : "var(--color-surface)",
                      border: "1px solid var(--color-border)",
                      opacity: d.supersededAt ? 0.75 : 1,
                      cursor: "pointer",
                      transition: "transform 0.1s, box-shadow 0.1s",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateY(-1px)";
                      e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.08)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "";
                      e.currentTarget.style.boxShadow = "";
                    }}
                    title={
                      d.supersededAt
                        ? "Eski sürüm (sonradan yeniden üretildi). Tıkla → indir."
                        : "Tıkla → indir"
                    }
                  >
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div style={{ fontWeight: 600, color: "var(--color-text)" }}>
                        📄 {documentTypeLabel(d)}
                        {isDraftPreview(d) ? (
                          <span
                            className="ml-2 px-2 py-0.5 rounded text-xs"
                            style={{
                              background:
                                "color-mix(in srgb, var(--color-primary) 12%, transparent)",
                              color: "var(--color-primary)",
                              fontWeight: 600,
                            }}
                          >
                            {DRAFT_PREVIEW_BADGE}
                          </span>
                        ) : null}
                        <span
                          style={{
                            marginLeft: 6,
                            fontFamily: "var(--font-mono)",
                            fontSize: "0.75rem",
                            color: "var(--color-text-3)",
                          }}
                        >
                          v{d.version.toString()}
                        </span>
                      </div>
                      {d.supersededAt ? (
                        <span
                          style={{
                            fontSize: "0.65rem",
                            padding: "2px 6px",
                            borderRadius: 4,
                            background: "var(--color-bg)",
                            color: "var(--color-text-3)",
                            whiteSpace: "nowrap",
                          }}
                        >
                          eski sürüm
                        </span>
                      ) : (
                        <span
                          style={{
                            fontSize: "0.65rem",
                            padding: "2px 6px",
                            borderRadius: 4,
                            background: "var(--color-success)",
                            color: "var(--color-surface)",
                            whiteSpace: "nowrap",
                            fontWeight: 600,
                          }}
                        >
                          güncel
                        </span>
                      )}
                    </div>
                    <div
                      style={{
                        color: "var(--color-text-3)",
                        fontSize: "0.78rem",
                        lineHeight: 1.5,
                      }}
                    >
                      <div>
                        {date} · {time}
                      </div>
                      <div>{(d.fileSizeBytes / 1024).toFixed(0)} KB</div>
                    </div>
                  </button>
                  {/* T22 — KPI tablosu (Excel): belge manifestinden (C5). TODO-KPI-13: yalnız
                      manifestli belgede (`hasKpiSnapshot`) çizilir — setsiz projede satır yok. */}
                  {d.hasKpiSnapshot ? <DocumentKpiLink documentId={d.id} /> : null}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Müşteri Katılımcı Rolü (Task 7) — izin-gated, projects.invite_client
          yoksa ParticipantsCard hiç render edilmez. */}
      <ParticipantsCard projectId={id} />

      {/* M9-S3 → M10: Proje-özel audit log (full-width, history'den sonra) */}
      <section className="mt-8">
        <ProjectAuditWidget projectId={id} />
      </section>

      <GenerateConfirmModal
        projectId={id}
        open={generateModalOpen}
        onClose={() => setGenerateModalOpen(false)}
        onStarted={() => {
          setGenerateModalOpen(false);
          void navigate(`/projects/${id}/generate/progress`);
        }}
      />
      <GenerateConfirmModal
        projectId={id}
        open={regenerateModalOpen}
        onClose={() => setRegenerateModalOpen(false)}
        onStarted={() => {
          setRegenerateModalOpen(false);
          void navigate(`/projects/${id}/generate/progress`);
        }}
        mode="regenerate_all"
        currentVersion={currentVersion}
      />
      <FillFromReportModal
        projectId={id}
        open={fillModalOpen}
        client={project.clientName}
        period={formatReportingPeriod(project.reportingYear, project.periodType)}
        framework={project.frameworkName}
        onClose={() => setFillModalOpen(false)}
        onQueued={() => {
          setFillModalOpen(false);
          setFillQueuedBand(true);
          setProvenanceRefreshKey((k) => k + 1);
        }}
      />
    </div>
  );
}

function initials(name: string): string {
  return name
    .split(" ")
    .map((s) => s[0] ?? "")
    .slice(0, 2)
    .join("")
    .toUpperCase();
}
